import styles from './style.module.scss';
import Image from 'components/image-loader/image';
import { useEffect, useRef, useState } from 'react';
import { useIsMobile } from 'hooks/use-size-class';

import type { SearchOurProperties } from '@tenants';

const DEFAULT_SCROLL_SPEED = 0.3;
const MOBILE_SCROLL_SPEED = 0.7;

type SearchOurPropertiesProps = SearchOurProperties;

const generateRandomWidths = (count: number, isMobile: boolean) => {
  const minWidth = isMobile ? 200 : 300;
  const maxWidth = isMobile ? 300 : 500;
  const minDifference = 50; // Minimum difference between consecutive widths
  const widths: number[] = [];

  for (let i = 0; i < count; i++) {
    let newWidth: number;
    let attempts = 0;
    const maxAttempts = 10;

    do {
      newWidth = Math.floor(Math.random() * (maxWidth - minWidth + 1)) + minWidth;
      attempts++;
    } while (
      attempts < maxAttempts &&
      widths.length > 0 &&
      Math.abs(newWidth - widths[widths.length - 1]) < minDifference
    );

    widths.push(newWidth);
  }

  return widths;
};

const ensureMinimumImages = (images: readonly string[], minCount: number = 10): string[] => {
  if (images.length >= minCount) {
    return [...images];
  }

  const repeatedImages = [...images];
  while (repeatedImages.length < minCount) {
    repeatedImages.push(...images);
  }

  // Trim to exact minimum count if we went over
  return repeatedImages.slice(0, minCount);
};

export default function SearchOurProperties({ title, linkText, linkPath, imageCarousel }: SearchOurPropertiesProps) {
  const isMobile = useIsMobile();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [isInView, setIsInView] = useState(false);
  const [sectionTop, setSectionTop] = useState(0);

  // Ensure we have at least 10 images
  const extendedImageCarousel = ensureMinimumImages(imageCarousel);
  const [randomWidths, setRandomWidths] = useState<number[]>([]);

  useEffect(() => {
    setRandomWidths(generateRandomWidths(extendedImageCarousel.length, isMobile));
  }, [extendedImageCarousel.length, isMobile]);

  // Intersection Observer to detect when section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting && sectionRef.current) {
          // Store the section's top position when it comes into view
          const rect = sectionRef.current.getBoundingClientRect();
          setSectionTop(window.scrollY + rect.top);
        }
      },
      {
        threshold: 0.15, // Trigger when 30% of the section is visible
        rootMargin: '0px 0px -10% 0px', // Start slightly before it's fully in view
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const currentRef = sectionRef.current;
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!isInView) return;

      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
        // Calculate scroll offset from when section came into view
        const scrollOffset = currentScrollTop - sectionTop;
        const scrollSpeed = isMobile ? MOBILE_SCROLL_SPEED : DEFAULT_SCROLL_SPEED;
        // Negative value to go left when scrolling down, positive when scrolling up
        const translateX = -(scrollOffset * scrollSpeed);
        scrollContainer.style.transform = `translateX(${translateX - 50}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInView, sectionTop, isMobile]);

  return (
    <div className={styles.container} ref={sectionRef}>
      <h2 dangerouslySetInnerHTML={{ __html: title.__html }} />

      <a href={linkPath}>{linkText}</a>

      <div data-testid="carousel" className={styles.imageCarousel} ref={scrollContainerRef}>
        {extendedImageCarousel.map((image, index) => (
          <div
            key={`${image}-${index}`}
            className={styles['img-container']}
            style={{ '--random-width': `${randomWidths[index]}px` } as React.CSSProperties}
          >
            <div>
              <Image src={image} alt={`Image ${index + 1}`} isVisible={isInView} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
